import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthProvider";
import { leads } from "@/lib/api";
import { FEATURE_GROUPS, RESOURCE_LINKS } from "@/lib/marketingNav";
import { useClickOutside } from "@/lib/useClickOutside";

// Build a seamless two-copy box-shadow starfield: points over 0..1000px in y are
// duplicated at +1000px so a translateY(-1000px) loop never shows a seam.
function starField(count, size) {
  const pts = Array.from({ length: count }, () => [
    Math.floor(Math.random() * 2000),
    Math.floor(Math.random() * 1000),
  ]);
  const shadow = pts
    .flatMap(([x, y]) => [`${x}px ${y}px #EAF2FF`, `${x}px ${y + 1000}px #EAF2FF`])
    .join(",");
  return { boxShadow: shadow, width: size, height: size };
}

export default function LandingPage() {
  const { user } = useAuth();
  const far = useMemo(() => starField(90, "1px"), []);
  const near = useMemo(() => starField(40, "2px"), []);
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const value = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      setErr("Enter a valid work email.");
      return;
    }
    setState("sending");
    try {
      await leads.create(value, "landing");
      setState("done");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Couldn't reach the server — please try again.");
      setState("idle");
    }
  };

  const [videoOk, setVideoOk] = useState(false);
  const [navOpen, setNavOpen] = useState(null); // "features" | "resources" | null
  const navRef = useRef(null);
  const toggleNav = (key) => setNavOpen((v) => (v === key ? null : key));
  const closeNav = () => setNavOpen(null);
  useClickOutside(navRef, closeNav, navOpen !== null);

  return (
    <div className="lp-root" data-testid="landing-page">
      {/* Fallback space background (also shows under reduced-motion / before the
          video can play): dark gradient + a subtle drifting starfield. */}
      <div className="lp-sky" aria-hidden="true">
        <div className="lp-stars-wrap lp-stars-far"><div className="lp-star" style={far} /></div>
        <div className="lp-stars-wrap lp-stars-near"><div className="lp-star" style={near} /></div>
      </div>

      {/* Real rocket-launch background video (CC0), full-bleed like arianespace.com */}
      <video
        className={`lp-video ${videoOk ? "is-ready" : ""}`}
        autoPlay muted loop playsInline preload="auto"
        poster="/launch-poster.jpg"
        aria-hidden="true"
        onCanPlay={() => setVideoOk(true)}
        onError={() => setVideoOk(false)}
      >
        <source src="/launch.mp4" type="video/mp4" />
      </video>
      {/* dark scrim over the video for headline legibility */}
      <div className="lp-scrim" aria-hidden="true" />

      {/* content */}
      <div className="lp-content">
        <header className="lp-nav">
          <div className="lp-logo"><span className="lp-hex">⬡</span> GovCo<span className="lp-logo-ai">.AI</span></div>

          <nav className="lp-links" ref={navRef}>
            <div className="lp-menu">
              <button
                className={`lp-trigger ${navOpen === "features" ? "active" : ""}`}
                onClick={() => toggleNav("features")}
                data-testid="lp-features-trigger"
              >
                Features <span className="lp-caret">▾</span>
              </button>
              {navOpen === "features" && (
                <div className="lp-panel" data-testid="lp-features-panel">
                  {FEATURE_GROUPS.map((group) => (
                    <Link key={group.slug} to={`/features/${group.slug}`} className="lp-item" onClick={closeNav}>
                      {group.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="lp-menu">
              <button
                className={`lp-trigger ${navOpen === "resources" ? "active" : ""}`}
                onClick={() => toggleNav("resources")}
                data-testid="lp-resources-trigger"
              >
                Resources <span className="lp-caret">▾</span>
              </button>
              {navOpen === "resources" && (
                <div className="lp-panel" data-testid="lp-resources-panel">
                  {RESOURCE_LINKS.map((item) => (
                    <Link key={item.href} to={item.href} className="lp-item" onClick={closeNav}>
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/pricing" className="lp-plain-link" data-testid="lp-pricing-link">Pricing</Link>
          </nav>

          {user ? (
            <Link className="lp-signin" to="/dashboard" data-testid="lp-dashboard">Go to dashboard →</Link>
          ) : (
            <Link className="lp-signin" to="/login" data-testid="lp-signin">Sign in →</Link>
          )}
        </header>

        <main className="lp-hero" id="demo">
          <div className="lp-eyebrow">◢ MISSION CONTROL FOR GOVCON</div>
          <h1 className="lp-title">
            Find and <span className="lp-ignite">Win</span> Government Contracts Using Artificial Intelligence
          </h1>
          <p className="lp-sub">
            GovCo.AI scans SAM.gov, shreds solicitations into compliance matrices, and drafts
            winning responses — so your team spends its time competing, not copy-pasting.
          </p>

          {state === "done" ? (
            <div className="lp-success" data-testid="lp-success">
              <div className="lp-success-title">🚀 You’re on the manifest.</div>
              <div className="lp-success-sub">We’ll email <strong>{email.trim()}</strong> to schedule your demo.</div>
            </div>
          ) : (
            <form className="lp-form" onSubmit={submit} noValidate>
              <div className="lp-field">
                <input
                  type="email"
                  className="lp-input"
                  placeholder="you@agency.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-label="Work email"
                  autoComplete="email"
                  data-testid="lp-email"
                />
                <button className="lp-cta" type="submit" disabled={state === "sending"} data-testid="lp-submit">
                  {state === "sending" ? "Scheduling…" : "Schedule Demo"}
                </button>
              </div>
              {err ? <div className="lp-err" data-testid="lp-err">{err}</div>
                   : <div className="lp-note">See it on your live pipeline. No card, no commitment.</div>}
            </form>
          )}

          <div className="lp-trust">
            <span>SAM.gov sync</span><i>·</i><span>Requirement shredding</span><i>·</i>
            <span>FAR-aware drafting</span><i>·</i><span>Audit-logged &amp; multi-tenant</span>
          </div>
        </main>
      </div>
    </div>
  );
}
